import { inject, NgModule } from "@angular/core";
import { Route, RouterModule, Routes } from "@angular/router";
import { AppLayoutComponent } from "./layout/app.layout.component";
import { ifUserAuthenticated } from "./core/auth/utils/if-user-authenticated.guard";
import { routes } from "../environments/environment.routes";
import { LOCAL_STORAGE_SCAN_MODE_KEY } from "./core/utils/app.constants.utils";
import { ScanLayoutComponent } from "./scan/components/scan-layout/scan-layout.component";
import { hasScannedProductsGuard } from "./scan/guards/has-scanned-products.guard";
import { nativeScanDeviceGuard } from "./scan/guards/native-scan-device.guard";
import { cameraRequiredGuard } from "./scan/guards/camera-required.guard";
import { alertHistoryEntryGuard } from "./features/alerts/guards/alert-history-entry.guard";
import { AlertHistorySessionService } from "./features/alerts/services/alert-history-session.service";
import { AuthService } from "./core/auth/services/auth.service";
import { betaFeatureGuard } from "./shared/guards/beta-feature.guard";
import { HIDE_BETA_FEATURES } from "./core/utils/hide-beta-features.token";

// The router hosts three independently authenticated apps:
// 1. Alert History (/alerts): entered through the backend login redirect
//    carrying token and logout_url query params (TWX-536), or with a regular
//    app session.
// 2. Item level scanning (/scan): embedded in a host app iframe and
//    authenticated per request with the token query param the host passes in.
// 3. The session app (everything else): classic login session guarded by
//    ifUserAuthenticated.
//
// Only /alerts, /scan and the not found page ship to production. The login
// flow and the session app sit behind betaFeatureGuard
// (environment.hideBetaFeatures), so in production those URLs never match and
// fall through to the `**` route, which lands on the not found page.

const ALERT_HISTORY_ROUTES: Route = {
  path: routes.frontend.alerts,
  component: AppLayoutComponent,
  canActivate: [alertHistoryEntryGuard],
  children: [
    {
      path: routes.frontend.mobileNeedMoreProductIssue,
      loadComponent: () =>
        import("./features/alerts/containers/mobile-need-more-product-issue-flow-container/mobile-need-more-product-issue-flow-container.component").then(
          (m) => m.MobileNeedMoreProductIssueFlowContainerComponent,
        ),
    },
    {
      path: routes.frontend.mobileServiceIssue,
      loadComponent: () =>
        import("./features/alerts/containers/mobile-service-issue-flow-container/mobile-service-issue-flow-container.component").then(
          (m) => m.MobileServiceIssueFlowContainerComponent,
        ),
    },
    {
      path: routes.frontend.mobileEquipmentIssue,
      loadComponent: () =>
        import("./features/alerts/containers/mobile-equipment-issue-flow-container/mobile-equipment-issue-flow-container.component").then(
          (m) => m.MobileEquipmentIssueFlowContainerComponent,
        ),
    },
    {
      path: routes.frontend.mobileCreditIssue,
      loadComponent: () =>
        import("./features/alerts/containers/mobile-credit-issue-flow-container/mobile-credit-issue-flow-container.component").then(
          (m) => m.MobileCreditIssueFlowContainerComponent,
        ),
    },
    {
      path: `${routes.frontend.alertsStore}/:storeId`,
      loadComponent: () =>
        import("./features/alerts/containers/alerts-list-container/alerts-list-container.component").then(
          (m) => m.AlertsListContainerComponent,
        ),
    },
    {
      path: "",
      pathMatch: "full",
      loadComponent: () =>
        import("./features/alerts/containers/stores-list-container/stores-list-container.component").then(
          (m) => m.StoresListContainerComponent,
        ),
    },
  ],
};

// Maintenance is hidden until the feature ships (nav entry is also commented
// out in app.menu.component.html). It belongs to the session app below.
// const MAINTENANCE_ROUTES: Route = {
//   path: routes.frontend.maintenance,
//   children: [
//     {
//       path: "users",
//       loadComponent: () =>
//         import("./features/user-management/containers/user-management-container.component").then(
//           (m) => m.UserManagementContainerComponent,
//         ),
//     },
//     { path: "", redirectTo: "users", pathMatch: "full" },
//   ],
// };

const ITEM_LEVEL_SCANNING_ROUTES: Route = {
  path: routes.frontend.itemLevelScanning,
  children: [
    {
      path: routes.frontend.itemLevelScanningScanType,
      loadComponent: () =>
        import("./features/item-level-scanning/containers/scan-type-container/scan-type-container.component").then(
          (m) => m.ScanTypeContainerComponent,
        ),
    },
    {
      path: routes.frontend.itemLevelScanningOutOfStock,
      loadComponent: () =>
        import("./features/item-level-scanning/containers/out-of-stock-container/out-of-stock-container.component").then(
          (m) => m.OutOfStockContainerComponent,
        ),
    },
    {
      path: routes.frontend.itemLevelScanningNeedsMerchandising,
      loadComponent: () =>
        import("./features/item-level-scanning/containers/needs-merchandising-container/needs-merchandising-container.component").then(
          (m) => m.NeedsMerchandisingContainerComponent,
        ),
    },
  ],
};

export const appRoutes: Routes = [
  { ...ALERT_HISTORY_ROUTES },
  {
    path: routes.frontend.scan,
    component: ScanLayoutComponent,
    children: [
      {
        path: routes.frontend.scanNative,
        canActivate: [nativeScanDeviceGuard],
        loadComponent: () =>
          import("./scan/containers/scan-native-container/scan-native-container.component").then(
            (m) => m.ScanNativeContainerComponent,
          ),
      },
      {
        path: routes.frontend.scanCamera,
        canActivate: [cameraRequiredGuard],
        loadComponent: () =>
          import("./scan/containers/scan-camera-container/scan-camera-container.component").then(
            (m) => m.ScanCameraContainerComponent,
          ),
      },
      {
        path: routes.frontend.scanResults,
        canActivate: [hasScannedProductsGuard],
        loadComponent: () =>
          import("./scan/containers/scanned-products-list-container/scanned-products-list-container.component").then(
            (m) => m.ScannedProductsListContainerComponent,
          ),
      },
      {
        path: routes.frontend.deviceNotSupported,
        loadComponent: () =>
          import("./scan/containers/device-not-supported-container/device-not-supported-container.component").then(
            (m) => m.DeviceNotSupportedContainerComponent,
          ),
      },
      {
        path: "",
        redirectTo: () => {
          const storedMode = localStorage.getItem(LOCAL_STORAGE_SCAN_MODE_KEY);
          return storedMode === "camera"
            ? routes.frontend.scanCamera
            : routes.frontend.scanNative;
        },
        pathMatch: "full",
      },
    ],
  },
  {
    path: routes.frontend.auth,
    canMatch: [betaFeatureGuard],
    data: { breadcrumb: "Auth" },
    loadChildren: () =>
      import("./core/auth/auth.module").then((m) => m.AuthModule),
  },
  {
    // Root landing: Alert History when any kind of session exists (regular
    // login or a stored TWX-536 entry), otherwise the login page. Without the
    // login page (beta features hidden) there is nowhere to send a visitor
    // but the not found page.
    path: "",
    pathMatch: "full",
    redirectTo: () => {
      const hasAppSession = !!inject(AuthService).userSession.value;
      const hasAlertHistorySession = inject(
        AlertHistorySessionService,
      ).hasSession;

      if (hasAppSession || hasAlertHistorySession) {
        return routes.frontend.alerts;
      }

      return inject(HIDE_BETA_FEATURES)
        ? routes.frontend.notfound
        : routes.frontend.login;
    },
  },
  {
    path: "",
    canMatch: [betaFeatureGuard],
    canActivate: [ifUserAuthenticated],
    component: AppLayoutComponent,
    children: [
      // { ...MAINTENANCE_ROUTES },
      { ...ITEM_LEVEL_SCANNING_ROUTES },
    ],
  },
  {
    path: routes.frontend.notfound,
    loadChildren: () =>
      import("./notfound/notfound.module").then((m) => m.NotfoundModule),
  },
  { path: "**", redirectTo: `/${routes.frontend.notfound}` },
];

@NgModule({
  imports: [RouterModule.forRoot(appRoutes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
